import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Pencil, Plus, Truck, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API, api, apiError, inr, logError, shortDate, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, ListState, PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DocumentJourney } from "@/components/DocumentJourney";
import { PartialDispatchDialog } from "@/components/DispatchActions";

const nextUid = () => `line-${Math.random().toString(36).slice(2, 10)}`;
const BLANK_INVOICE_LINE = { name: "", quantity: 1, unit: "Nos", rate: 0, gst_rate: 18, discount_percent: 0, hsn_sac: "9506" };

function invoiceStatusVariant(status) {
  if (status === "Paid") return "default";
  if (status === "Cancelled") return "destructive";
  return "outline";
}

export function Expenses() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), category: "Manufacturing", subcategory: "",
    amount: "", gst_amount: "", vendor: "", vendor_gstin: "", payment_method: "Bank",
    project_id: "", project_name: "", invoice_number: "", description: "", receipt_file_id: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/expenses"), api.get("/expense-categories")]);
      setData(a.data);
      setCats(b.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get("/projects")
      .then(({ data: projectList }) => setProjects(projectList))
      .catch((e) => logError("project list", e));
  }, [load]);

  const onReceipt = async (file) => {
    if (!file) return;
    setOcrBusy(true);
    try {
      const up = await uploadFile(file, "expense");
      setForm((f) => ({ ...f, receipt_file_id: up.id }));
      const { data: ocr } = await api.post("/expenses/ocr", { file_id: up.id });
      setForm((f) => ({
        ...f,
        vendor: ocr.vendor || f.vendor,
        invoice_number: ocr.invoice_number || f.invoice_number,
        date: ocr.date || f.date,
        vendor_gstin: ocr.gstin || f.vendor_gstin,
        amount: ocr.amount ?? f.amount,
        gst_amount: ocr.gst_amount ?? f.gst_amount,
        category: ocr.category || f.category,
        subcategory: ocr.subcategory || f.subcategory,
      }));
      toast.success(`Extracted with ${ocr.confidence ?? "?"}% confidence — please verify before saving`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setOcrBusy(false);
    }
  };

  const save = async () => {
    try {
      if (!Number(form.amount)) return toast.error("Enter the expense amount");
      const project = projects.find((p) => p.id === form.project_id);
      await api.post("/expenses", {
        ...form,
        amount: Number(form.amount),
        gst_amount: Number(form.gst_amount || 0),
        project_name: project?.name || "",
      });
      toast.success("Expense recorded");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="expenses-header"
        title="Expenses"
        subtitle={`${data.items.length} entries • ${inr(data.total)} total`}
        actions={
          can("expenses", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-expense-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>New expense</DialogTitle>
                </DialogHeader>
                <label className="flex cursor-pointer items-center gap-2 rounded border border-dashed border-border p-3 text-sm text-muted-foreground hover:bg-secondary">
                  {ocrBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Upload bill photograph — AI extracts the details for your verification
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    data-testid="expense-receipt-input"
                    onChange={(e) => onReceipt(e.target.files?.[0])}
                  />
                </label>
                <label className="flex cursor-pointer items-center gap-2 rounded border border-dashed border-border p-3 text-sm text-muted-foreground hover:bg-secondary">
                  <Upload className="h-4 w-4" />
                  Snap the bill with your phone camera
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    data-testid="expense-camera-input"
                    onChange={(e) => onReceipt(e.target.files?.[0])}
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Date">
                    <Input
                      type="date"
                      data-testid="expense-date-input"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </Field>
                  <Field label="Amount ₹">
                    <Input
                      data-testid="expense-amount-input"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                  </Field>
                  <Field label="Category">
                    <Select
                      value={form.category}
                      onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}
                    >
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(cats).map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Subcategory">
                    <Select value={form.subcategory} onValueChange={(v) => setForm({ ...form, subcategory: v })}>
                      <SelectTrigger data-testid="expense-subcategory-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {(cats[form.category] || []).map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="GST amount ₹">
                    <Input value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} />
                  </Field>
                  <Field label="Vendor">
                    <Input
                      data-testid="expense-vendor-input"
                      value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                    />
                  </Field>
                  <Field label="Vendor GSTIN">
                    <Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value })} />
                  </Field>
                  <Field label="Payment method">
                    <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Cash", "Bank", "UPI", "Cheque", "Card", "Other"].map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Project" className="sm:col-span-2">
                    <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                      <SelectTrigger data-testid="expense-project-select">
                        <SelectValue placeholder="Link to a project (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Description" className="sm:col-span-2">
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </Field>
                </div>
                <DialogFooter>
                  <Button data-testid="expense-save-button" onClick={save}>
                    Save expense
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <div className="p-4 sm:p-6">
        <ListState loading={loading} isEmpty={data.items.length === 0}
          emptyTitle="No expenses recorded">
            <div className="surface overflow-x-auto" data-testid="expenses-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="hidden md:table-cell">Vendor</TableHead>
                    <TableHead className="hidden lg:table-cell">Project</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((e) => (
                    <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                      <TableCell className="text-xs">{shortDate(e.date)}</TableCell>
                      <TableCell className="text-sm">
                        {e.category}
                        <div className="text-xs text-muted-foreground">{e.subcategory}</div>
                      </TableCell>
                      <TableCell className="hidden text-sm md:table-cell">{e.vendor || "—"}</TableCell>
                      <TableCell className="hidden text-xs lg:table-cell">{e.project_name || "—"}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{inr(e.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </ListState>
      </div>
    </div>
  );
}

export function Invoices() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [partDispatch, setPartDispatch] = useState(null);
  const [journey, setJourney] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "Bank", reference: "", kind: "part" });
  const [form, setForm] = useState({
    customer_id: "", kind: "tax_invoice", gst_mode: "auto", project_name: "",
    items: [{ uid: nextUid(), ...BLANK_INVOICE_LINE }],
    installation_charge: 0, transportation_charge: 0, other_charges: 0, charges_gst_rate: 18,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/invoices"),
        api.get("/sales-orders").catch(() => ({ data: [] })),
        api.get("/customers?limit=200"),
      ]);
      setInvoices(a.data);
      setOrders(b.data);
      setCustomers(c.data.items);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fromOrder = (orderId) => {
    const o = orders.find((x) => x.id === orderId);
    if (!o) return;
    setForm({
      ...form,
      customer_id: o.customer_id,
      sales_order_id: o.id,
      project_name: o.project_name || "",
      items: (o.items || []).map((i) => ({
        uid: nextUid(),
        name: i.name, quantity: i.quantity, unit: i.unit, rate: i.rate,
        gst_rate: i.gst_rate, discount_percent: i.discount_percent || 0, hsn_sac: i.hsn_sac || "",
      })),
    });
  };

  const save = async () => {
    try {
      if (!form.customer_id) return toast.error("Select a customer");
      const items = form.items
        .filter((i) => i.name.trim())
        .map(({ uid, ...i }) => ({
          ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0),
          gst_rate: Number(i.gst_rate || 0), discount_percent: Number(i.discount_percent || 0),
        }));
      if (!items.length) return toast.error("Add at least one line item");
      const { data } = await api.post("/invoices", { ...form, items });
      toast.success(`Invoice ${data.number} created`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePayment = async () => {
    try {
      await api.post("/payments", {
        invoice_id: payOpen.id,
        customer_id: payOpen.customer_id,
        customer_name: payOpen.customer_name,
        amount: Number(pay.amount),
        method: pay.method,
        reference: pay.reference,
        kind: pay.kind,
      });
      toast.success("Payment recorded");
      setPayOpen(null);
      setPay({ amount: "", method: "Bank", reference: "", kind: "part" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="invoices-header"
        title="Invoices"
        subtitle="GST tax invoices, proforma and advance billing with payment tracking."
        actions={
          can("invoices", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-invoice-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>New invoice</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Create from sales order">
                    <Select onValueChange={fromOrder}>
                      <SelectTrigger data-testid="invoice-order-select">
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent>
                        {orders.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.number} — {o.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Customer">
                    <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                      <SelectTrigger data-testid="invoice-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Invoice type">
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger data-testid="invoice-kind-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[["tax_invoice", "Tax Invoice"], ["proforma", "Proforma Invoice"], ["advance", "Advance Invoice"], ["credit_note", "Credit Note"], ["debit_note", "Debit Note"]].map(([v, l]) => (
                          <SelectItem key={v} value={v}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="GST type">
                    <Select value={form.gst_mode} onValueChange={(v) => setForm({ ...form, gst_mode: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatic (from customer state)</SelectItem>
                        <SelectItem value="intra">CGST + SGST</SelectItem>
                        <SelectItem value="inter">IGST</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                {form.items.map((it, i) => (
                  <div key={it.uid} className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    <Field label="Description" className="col-span-2">
                      <Input
                        data-testid={`invoice-item-name-${i}`}
                        value={it.name}
                        onChange={(e) => {
                          const items = [...form.items];
                          items[i] = { ...items[i], name: e.target.value };
                          setForm({ ...form, items });
                        }}
                      />
                    </Field>
                    {[["quantity", "Qty"], ["rate", "Rate ₹"], ["gst_rate", "GST %"]].map(([k, l]) => (
                      <Field key={k} label={l}>
                        <Input
                          data-testid={`invoice-item-${k}-${i}`}
                          value={it[k]}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[i] = { ...items[i], [k]: e.target.value };
                            setForm({ ...form, items });
                          }}
                        />
                      </Field>
                    ))}
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="invoice-add-line-button"
                  onClick={() =>
                    setForm({
                      ...form,
                      items: [...form.items, { uid: nextUid(), ...BLANK_INVOICE_LINE }],
                    })
                  }
                >
                  Add line
                </Button>
                <DialogFooter>
                  <Button data-testid="invoice-save-button" onClick={save}>
                    Create invoice
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <div className="p-4 sm:p-6">
        {journey && (
          <div className="mb-3">
            <DocumentJourney entity="invoice" id={journey} />
          </div>
        )}
        {partDispatch && (
          <PartialDispatchDialog invoice={partDispatch} onClose={() => setPartDispatch(null)}
            onCreated={() => navigate("/dispatch")} />
        )}
        <ListState loading={loading} isEmpty={invoices.length === 0}
          emptyTitle="No invoices yet"
          emptyHint="Convert an approved quotation to an order, then raise the invoice.">
            <div className="surface overflow-x-auto" data-testid="invoices-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono-num text-xs">
                        {inv.number}
                        <div className="text-[10px] text-muted-foreground">{shortDate(inv.date)}</div>
                      </TableCell>
                      <TableCell className="text-sm">{inv.customer_name}</TableCell>
                      <TableCell className="text-xs">{String(inv.kind).replace(/_/g, " ")}</TableCell>
                      <TableCell>
                        <Badge variant={invoiceStatusVariant(inv.status)} className="text-[10px]">
                          {inv.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{inr(inv.grand_total)}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm text-accent">
                        {inr(Number(inv.grand_total) - Number(inv.paid_amount || 0))}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          data-testid={`invoice-pdf-button-${inv.id}`}
                          onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")}
                        >
                          <Download className="h-3.5 w-3.5" /> PDF
                        </Button>
                        {inv.status !== "Paid" && inv.status !== "Cancelled" && can("invoices", "edit") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            data-testid={`invoice-edit-button-${inv.id}`}
                            onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                          >
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                        )}
                        {inv.status !== "Paid" && inv.status !== "Cancelled" && can("payments", "create") && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`invoice-pay-button-${inv.id}`}
                            onClick={() => setPayOpen(inv)}
                          >
                            Record payment
                          </Button>
                        )}
                        {inv.status !== "Cancelled" && inv.kind === "tax_invoice" && can("dispatch", "create") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            data-testid={`invoice-part-dispatch-button-${inv.id}`}
                            onClick={() => setPartDispatch(inv)}
                          >
                            <Truck className="h-3.5 w-3.5" /> Dispatch
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`invoice-journey-button-${inv.id}`}
                          onClick={() => setJourney(journey === inv.id ? null : inv.id)}
                        >
                          Journey
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </ListState>
      </div>

      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>
              Record payment — {payOpen?.number} (outstanding{" "}
              {inr(Number(payOpen?.grand_total) - Number(payOpen?.paid_amount || 0))})
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Amount ₹">
              <Input
                data-testid="payment-amount-input"
                value={pay.amount}
                onChange={(e) => setPay({ ...pay, amount: e.target.value })}
              />
            </Field>
            <Field label="Method">
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Cash", "Bank", "UPI", "Cheque", "Other"].map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Type">
              <Select value={pay.kind} onValueChange={(v) => setPay({ ...pay, kind: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["advance", "part", "final"].map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Reference">
              <Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button data-testid="payment-save-button" onClick={savePayment}>
              Save payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments");
      setPayments(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div>
      <PageHeader
        testId="payments-header"
        title="Payments & Receipts"
        subtitle={`${payments.length} receipts • ${inr(total)} collected`}
      />
      <div className="p-4 sm:p-6">
        <ListState loading={loading} isEmpty={payments.length === 0}
          emptyTitle="No payments recorded"
          emptyHint="Record a payment against an invoice to issue a receipt.">
            <div className="surface overflow-x-auto" data-testid="payments-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">Invoice</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                      <TableCell className="font-mono-num text-xs">
                        {p.number}
                        <div className="text-[10px] text-muted-foreground">{shortDate(p.date)}</div>
                      </TableCell>
                      <TableCell className="text-sm">{p.customer_name || "—"}</TableCell>
                      <TableCell className="hidden font-mono-num text-xs md:table-cell">
                        {p.invoice_number || "On account"}
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">{p.method}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{inr(p.amount)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          data-testid={`receipt-pdf-button-${p.id}`}
                          onClick={() => window.open(`${API}/payments/${p.id}/pdf`, "_blank")}
                        >
                          <Download className="h-3.5 w-3.5" /> Receipt
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </ListState>
      </div>
    </div>
  );
}

export function Receivables() {
  const [data, setData] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/receivables")
        .then(({ data: d }) => setData(d))
        .catch((e) => toast.error(apiError(e))),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const remind = async (id) => {
    try {
      const { data: r } = await api.post(`/reminders/invoice/${id}/send`, { channel: "both" });
      if (r.delivered?.length) toast.success(`Reminder sent by ${r.delivered.join(" & ")}`);
      else toast.info("Reminder logged — opening WhatsApp with the ready message");
      if (r.whatsapp?.wa_link) window.open(r.whatsapp.wa_link, "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div>
      <PageHeader
        testId="receivables-header"
        title="Receivables"
        subtitle={`${inr(data.total)} outstanding across ${data.rows.length} invoices`}
      />
      <div className="grid grid-cols-2 border-b border-border sm:grid-cols-5" data-testid="ageing-buckets">
        {Object.entries(data.ageing).map(([k, v]) => (
          <div key={k} className="kpi border-b border-r border-border">
            <div className="overline">{k === "current" ? "Current" : `${k} days`}</div>
            <div className="mt-1.5 font-mono-num text-xl font-semibold">{inr(v)}</div>
          </div>
        ))}
      </div>
      <div className="p-4 sm:p-6">
        {data.rows.length === 0 ? (
          <Empty title="Nothing outstanding" />
        ) : (
          <div className="surface overflow-x-auto" data-testid="receivables-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Bucket</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono-num text-xs">{r.number}</TableCell>
                    <TableCell className="text-sm">{r.customer_name}</TableCell>
                    <TableCell className="text-xs">
                      {shortDate(r.due_date)}
                      {r.days_overdue > 0 && (
                        <Badge variant="destructive" className="ml-1 text-[9px]">
                          {r.days_overdue}d
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.bucket}</TableCell>
                    <TableCell className="text-right font-mono-num text-sm">{inr(r.outstanding)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`reminder-button-${r.id}`}
                        onClick={() => remind(r.id)}
                      >
                        Send reminder
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
